import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch, ApiError } from "../lib/apiClient";
import { PrimaryEvidenceFile, Registrant, StatutoryCompletion } from "../lib/types";
import CopyButton from "../components/CopyButton";
import TypeBadge from "../components/TypeBadge";

type ShowResponse = { registrant: Registrant };
type PrimaryListResponse = { registrant_pin: string; primary_evidence: PrimaryEvidenceFile[] };

const STATUSES = ["complete", "expired", "missing", "awaiting_review"] as const;
const TYPES = ["statutory", "mandatory", "other"] as const;
const PAGE_SIZE = 50;

export default function RegistrantDetailPage() {
  const { pin = "" } = useParams();
  const navigate = useNavigate();
  const [registrant, setRegistrant] = useState<Registrant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [statuses, setStatuses] = useState<Set<string>>(new Set());
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState<string>("");
  const [page, setPage] = useState(1);

  async function load() {
    setError(null);
    try {
      // Use the dedicated show endpoint — scoped to permissions, faster than
      // pulling the whole list and finding by pin.
      const resp = await apiFetch<ShowResponse>(`/api/v1/registrants/${encodeURIComponent(pin)}`);
      setRegistrant(resp.registrant ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
    }
  }

  useEffect(() => { load(); }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  const completions = registrant?.statutory_completions ?? [];

  const categoryOptions = useMemo(() => {
    const s = new Set<string>();
    completions.forEach(c => { if (c.category) s.add(c.category); });
    return Array.from(s).sort();
  }, [completions]);

  const filtered = useMemo(() => {
    return completions.filter(c => {
      if (statuses.size && !statuses.has(c.status)) return false;
      if (types.size && !types.has(c.statutory_type)) return false;
      if (category && c.category !== category) return false;
      return true;
    });
  }, [completions, statuses, types, category]);

  useEffect(() => { setPage(1); }, [statuses, types, category]);

  async function del(completionId: string) {
    if (!confirm("Delete this completion?")) return;
    setDeletingId(completionId);
    try {
      await apiFetch(`/api/v1/completions/${completionId}`, { method: "DELETE" });
      await load();
    } catch (e) {
      if (e instanceof ApiError) alert(`${e.code}: ${e.message}`);
      else alert(String(e));
    } finally {
      setDeletingId(null);
    }
  }

  function toggle(set: Set<string>, value: string): Set<string> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    return next;
  }

  function clearFilters() {
    setStatuses(new Set());
    setTypes(new Set());
    setCategory("");
  }

  const activeFilterCount = statuses.size + types.size + (category ? 1 : 0);

  if (error) return <div className="text-red-600 bg-red-50 border border-red-200 rounded p-3 text-sm">{error}</div>;
  if (!registrant) return <div className="text-slate-500">Loading…</div>;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paged = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const displayName = registrant.profile
    ? `${registrant.profile.first_name} ${registrant.profile.last_name}`
    : registrant.first_name && registrant.last_name
      ? `${registrant.first_name} ${registrant.last_name}`
      : registrant.pin;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div>
          <Link to="/registrants" className="text-sm text-brand-700 hover:underline">← Back</Link>
          <h1 className="text-xl font-semibold mt-1 flex items-center gap-2">
            {displayName}
            <span className="mono text-slate-400 text-base">{registrant.pin}</span>
            <CopyButton value={registrant.pin} />
          </h1>
          <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
            <span>
              {registrant.grade ?? "—"}
              {" · "}
              {registrant.approved ? "approved" : "pending"}
            </span>
            {registrant.sponsored && (
              <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                Sponsored
              </span>
            )}
            {registrant.sponsorship_pending && (
              <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-700 border border-amber-200">
                Sponsorship invite pending
              </span>
            )}
            {!registrant.profile && (
              <span
                className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-600 border border-slate-200"
                title="Your key does not have profile.read permission, so personal fields (email, DoB, address) are not exposed."
              >
                Profile read not granted
              </span>
            )}
          </div>
        </div>
        <Link
          to={`/registrants/${registrant.pin}/completions/new`}
          className="ml-auto bg-brand-600 text-white text-sm rounded px-3 py-2"
        >
          + New completion
        </Link>
      </div>

      <ProfilePanel registrant={registrant} />

      <PrimaryEvidencePanel pin={registrant.pin} />

      <h2 className="text-sm font-semibold text-slate-700 mt-6">Statutory & Mandatory</h2>

      {registrant.statutory_completions === undefined ? (
        <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-600">
          Your key does not have any <code className="mono">statutory_mandatory.read</code> permission, so S&amp;M completions are not exposed.
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded p-3 flex flex-wrap gap-4 items-start text-sm">
            <FilterChipGroup
              label="Status"
              options={STATUSES as readonly string[]}
              selected={statuses}
              onToggle={v => setStatuses(prev => toggle(prev, v))}
            />
            <FilterChipGroup
              label="Type"
              options={TYPES as readonly string[]}
              selected={types}
              onToggle={v => setTypes(prev => toggle(prev, v))}
            />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Category</div>
              <select
                className="border border-slate-300 rounded px-2 py-1 text-sm min-w-[220px]"
                value={category}
                onChange={e => setCategory(e.target.value)}
              >
                <option value="">— any —</option>
                {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="ml-auto flex gap-2 items-center">
              <span className="text-slate-500 text-xs">
                {filtered.length !== completions.length
                  ? `${filtered.length} of ${completions.length}`
                  : `${completions.length} total`}
              </span>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-800">
                  Clear ({activeFilterCount})
                </button>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Cert type</th>
                  <th className="text-left font-medium px-3 py-2">Type</th>
                  <th className="text-left font-medium px-3 py-2">Category</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="text-left font-medium px-3 py-2">Acquired</th>
                  <th className="text-left font-medium px-3 py-2">Expires</th>
                  <th className="text-left font-medium px-3 py-2">Files</th>
                  <th className="text-left font-medium px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {paged.map(c => (
                  <CompletionRow
                    key={c.cert_type_slug}
                    c={c}
                    onEdit={id => navigate(`/completions/${id}/edit`)}
                    onDelete={del}
                    deleting={deletingId}
                  />
                ))}
                {paged.length === 0 && (
                  <tr><td colSpan={8} className="p-4 text-sm text-slate-500 text-center">
                    {activeFilterCount > 0 ? "No completions match the filters." : "No cert types required for this registrant."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pageSafe}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}

function ProfilePanel({ registrant }: { registrant: Registrant }) {
  const p = registrant.profile;
  if (!p) return null;
  const addressParts = [
    p.address.line_1, p.address.line_2, p.address.line_3,
    p.address.city, p.address.county, p.address.postcode, p.address.country
  ].filter(Boolean).join(", ");

  return (
    <div className="bg-white border border-slate-200 rounded p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Profile (profile.read)</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <Field label="Name">{[p.salutation, p.first_name, p.last_name].filter(Boolean).join(" ")}</Field>
        <Field label="Email">{p.email}</Field>
        <Field label="Phone">{p.contact_number ?? "—"}</Field>
        <Field label="Date of birth">{p.date_of_birth ?? "—"}</Field>
        <Field label="Address" wide>{addressParts || "—"}</Field>
      </div>
    </div>
  );
}

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}

function PrimaryEvidencePanel({ pin }: { pin: string }) {
  const [files, setFiles] = useState<PrimaryEvidenceFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const navigate = useNavigate();

  async function load() {
    setError(null);
    setCode(null);
    try {
      const resp = await apiFetch<PrimaryListResponse>(`/api/v1/registrants/${encodeURIComponent(pin)}/primary_evidence`);
      setFiles(resp.primary_evidence ?? []);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        setCode(e.code);
      } else {
        setError(e instanceof Error ? e.message : "Request failed");
      }
      setFiles(null);
    }
  }

  useEffect(() => { load(); }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  async function del(file: PrimaryEvidenceFile) {
    if (!confirm(`Delete primary evidence "${file.name ?? file.id}"?`)) return;
    setDeletingId(file.id);
    try {
      await apiFetch(`/api/v1/primary_evidence/${file.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      if (e instanceof ApiError) alert(`${e.code}: ${e.message}`);
      else alert(String(e));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3 mb-2">
        <h2 className="text-sm font-semibold text-slate-700">Primary Evidence</h2>
        <span className="text-xs text-slate-500">primary_evidence section</span>
        <Link
          to={`/registrants/${pin}/primary-evidence/new`}
          className="ml-auto bg-brand-600 text-white text-xs rounded px-3 py-1.5"
        >
          + Push file
        </Link>
      </div>

      {error && (
        <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-600">
          {code === "insufficient_permissions"
            ? <>Your key does not have primary evidence read permission ({code}). Grant <code className="mono">primary_evidence.read.own</code> and/or <code className="mono">primary_evidence.read.others</code> from the HPAC admin.</>
            : <>{error}</>}
        </div>
      )}

      {files !== null && (
        <div className="bg-white border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-3 py-2">Name</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-left font-medium px-3 py-2">Acquired</th>
                <th className="text-left font-medium px-3 py-2">Expires</th>
                <th className="text-left font-medium px-3 py-2">Author</th>
                <th className="text-left font-medium px-3 py-2">Files</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {files.map(f => (
                <tr key={f.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 align-top">
                    <div>{f.name ?? "—"}</div>
                    <div className="text-[11px] text-slate-400 mono">#{f.id}</div>
                  </td>
                  <td className="px-3 py-2 align-top">{f.status}</td>
                  <td className="px-3 py-2 align-top">{fmtDate(f.acquired_on)}</td>
                  <td className="px-3 py-2 align-top">{fmtDate(f.expires_on)}</td>
                  <td className="px-3 py-2 align-top">
                    <AuthorBadge file={f} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    {f.files.length === 0
                      ? <span className="text-slate-400">—</span>
                      : (
                        <ul className="space-y-0.5">
                          {f.files.map((url, i) => (
                            <li key={i}>
                              <a href={url} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline text-xs mono break-all">
                                {url.split("/").pop() ?? url}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    <div className="flex gap-3 justify-end">
                      <button
                        className="text-xs text-brand-700 hover:text-brand-900"
                        onClick={() => navigate(`/primary-evidence/${f.id}/edit`)}
                      >
                        Edit
                      </button>
                      <button
                        className="text-xs text-red-600 hover:text-red-800 disabled:text-slate-400"
                        onClick={() => del(f)}
                        disabled={deletingId === f.id || f.scope === "others"}
                        title={f.scope === "others" ? "API can never delete HPAC/registrant-authored Primary Evidence." : undefined}
                      >
                        {deletingId === f.id ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {files.length === 0 && (
                <tr><td colSpan={7} className="p-4 text-sm text-slate-500 text-center">
                  No primary evidence files visible. Push one with <strong>+ Push file</strong>.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AuthorBadge({ file }: { file: PrimaryEvidenceFile }) {
  const baseScope = file.scope === "own" ? "bg-brand-50 text-brand-700 border-brand-100" : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded border ${baseScope}`}>
        {file.scope}
      </span>
      {file.authored_via_api && (
        <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-700 border border-amber-200" title="Created via the Developer Connections API">
          via API
        </span>
      )}
    </div>
  );
}

function CompletionRow({
  c,
  onEdit,
  onDelete,
  deleting,
}: {
  c: StatutoryCompletion;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  deleting: string | null;
}) {
  const colour = statusColour(c.status);
  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-2 align-top">
        <div className="flex items-center gap-1">
          <span>{c.cert_type_name}</span>
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="mono text-[11px] text-slate-500">{c.cert_type_slug}</span>
          <CopyButton value={c.cert_type_slug} />
        </div>
        <ScopeBadge scope={c.scope} />
      </td>
      <td className="px-3 py-2 align-top"><TypeBadge type={c.statutory_type} /></td>
      <td className="px-3 py-2 text-slate-500 align-top">{c.category ?? "—"}</td>
      <td className={`px-3 py-2 font-medium align-top ${colour}`}>{c.status}</td>
      <td className="px-3 py-2 align-top">{fmtDate(c.acquired_on)}</td>
      <td className="px-3 py-2 align-top">{fmtDate(c.expires_on)}</td>
      <td className="px-3 py-2 align-top">
        {c.files.length === 0
          ? <span className="text-slate-400">—</span>
          : (
            <ul className="space-y-0.5">
              {c.files.map((url, i) => (
                <li key={i}>
                  <a href={url} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline text-xs mono break-all">
                    {url.split("/").pop() ?? url}
                  </a>
                </li>
              ))}
            </ul>
          )}
      </td>
      <td className="px-3 py-2 text-right align-top">
        {c.evidence_file_id ? (
          <div className="flex gap-3 justify-end">
            <button
              className="text-xs text-brand-700 hover:text-brand-900"
              onClick={() => onEdit(String(c.evidence_file_id))}
            >
              Edit
            </button>
            <button
              className="text-xs text-red-600 hover:text-red-800"
              onClick={() => onDelete(String(c.evidence_file_id))}
              disabled={deleting === String(c.evidence_file_id)}
            >
              {deleting === String(c.evidence_file_id) ? "…" : "Delete"}
            </button>
          </div>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
    </tr>
  );
}

function ScopeBadge({ scope }: { scope: "org" | "hpac_baseline" }) {
  if (scope === "hpac_baseline") {
    return (
      <span
        className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-700 border border-slate-200"
        title="Defined by HPAC centrally; applies to this registrant regardless of your org's configuration."
      >
        HPAC
      </span>
    );
  }
  return (
    <span
      className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] rounded bg-brand-50 text-brand-700 border border-brand-100"
      title="Defined by your organisation."
    >
      Org
    </span>
  );
}

function FilterChipGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly string[];
  selected: Set<string>;
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {options.map(opt => {
          const on = selected.has(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={`px-2 py-1 rounded text-xs border ${
                on
                  ? "bg-brand-600 text-white border-brand-600"
                  : "bg-white text-slate-700 border-slate-300 hover:border-brand-500"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Pagination({
  page, totalPages, totalItems, pageSize, onChange,
}: { page: number; totalPages: number; totalItems: number; pageSize: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-slate-500">{from}–{to} of {totalItems}</span>
      <div className="ml-auto flex items-center gap-1">
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === 1} onClick={() => onChange(1)}>«</button>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
        <span className="px-2">Page {page} / {totalPages}</span>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === totalPages} onClick={() => onChange(page + 1)}>›</button>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === totalPages} onClick={() => onChange(totalPages)}>»</button>
      </div>
    </div>
  );
}

function statusColour(s: string): string {
  switch (s) {
    case "complete": return "text-emerald-700";
    case "expired": return "text-red-600";
    case "missing": return "text-amber-600";
    case "awaiting_review": return "text-sky-600";
    default: return "text-slate-600";
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}
